import React from 'react';
import site from '../../data/site';
import { ambienceClip } from '../../data/media';
import { editorial } from '../../data/collections';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import DotTrail from '../ui/DotTrail';
import Reveal from '../ui/Reveal';
import AmbientVideo from '../ui/AmbientVideo';
import Parallax from '../ui/Parallax';
import { RevealLine } from '../ui/TextReveal';

const marks = [
  { icon: 'seal', label: site.credential },
  { icon: 'gem', label: 'Gold · Diamond · Antique' },
  { icon: 'pin', label: 'C G Road, Ahmedabad' },
];

/**
 * Opening statement: the tagline set large against the showroom's own
 * footage, with the counter-side promise ("come in, we will show you") as the
 * primary call to action rather than a checkout button.
 */
export function Hero() {
  const clip = ambienceClip('ambience-01');
  const portrait = editorial[0];

  return (
    <section id="top" className="relative overflow-hidden bg-canvas pt-28 lg:pt-32">
      {/* Sunrise bloom, borrowed from the mark. */}
      <div
        className="pointer-events-none absolute -right-32 -top-24 h-[38rem] w-[38rem] rounded-full opacity-45 blur-3xl"
        style={{ background: 'radial-gradient(circle, #ffdb85 0%, #fbf9f5 70%)' }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -left-40 top-1/3 h-[30rem] w-[30rem] rounded-full opacity-25 blur-3xl"
        style={{ background: 'radial-gradient(circle, #d6b369 0%, transparent 70%)' }}
        aria-hidden="true"
      />

      <div className="shell relative grid items-center gap-14 pb-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20 lg:pb-28">
        {/* ---- Copy ---- */}
        <div className="max-w-xl">
          <Reveal className="flex items-center gap-3">
            <DotTrail count={5} />
            <span className="eyebrow">Gold · Diamond · Antique · Ahmedabad</span>
          </Reveal>

          {/* Each line is masked separately so they slide up in sequence. */}
          <h1 className="mt-6 font-display text-[3rem] leading-[0.95] text-ink-950 sm:text-[4.2rem] lg:text-[5.1rem]">
            <RevealLine delay={80}>A place where</RevealLine>
            <RevealLine delay={210} bleed="0.5em">
              people
              <span className="relative ml-4 inline-block">
                {/* bg-clip-text needs background-image, not the `background`
                    shorthand — the shorthand resets background-clip. */}
                <span className="relative z-10 bg-[linear-gradient(120deg,#f49100,#f9af00_45%,#c99f4d)] bg-clip-text italic text-transparent">
                  trust
                </span>
                {/* Hand-drawn underline, echoing the curve in the mark. It
                    draws itself in once the line has finished arriving. */}
                <svg
                  className="absolute -bottom-2 left-0 w-full text-sun-400/70"
                  viewBox="0 0 200 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 9.5C40 3.5 92 2.5 198 6"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    pathLength="1"
                    className="[stroke-dasharray:1] [stroke-dashoffset:1] animate-draw-line"
                  />
                </svg>
              </span>
            </RevealLine>
          </h1>

          <Reveal delay={150}>
            <p className="mt-8 max-w-md text-[1.02rem] leading-relaxed text-ink-700/85">
              Gold, diamond and antique jewellery, shown to you across the counter the way it
              always has been — unhurried, explained, and valued by a Govt. Approved Valuer.
            </p>
          </Reveal>

          <Reveal delay={220} className="mt-10 flex flex-wrap items-center gap-3">
            <Button href="#collections" variant="solid" size="lg">
              View Collections
              <Icon name="arrowRight" size={17} />
            </Button>
            <Button href={site.contact.whatsappHref} variant="outline" size="lg">
              <Icon name="whatsapp" size={17} />
              Book a Viewing
            </Button>
          </Reveal>

          <Reveal delay={300} className="mt-12">
            <ul className="flex flex-wrap gap-x-8 gap-y-4">
              {marks.map((mark) => (
                <li key={mark.label} className="flex items-center gap-2.5">
                  <Icon name={mark.icon} size={17} className="text-champagne-500" />
                  <span className="text-[0.7rem] uppercase tracking-widest text-ink-700/70">
                    {mark.label}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* ---- Visual: showroom footage with an overlapping campaign frame ---- */}
        <Reveal
          delay={160}
          variant="scale"
          duration={1100}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div className="relative">
            {/* Champagne offset frame. */}
            <div
              className="absolute -right-3 -top-3 bottom-6 left-6 rounded-sm border border-champagne-300/55"
              aria-hidden="true"
            />

            <AmbientVideo
              src={clip.src}
              poster={clip.poster}
              alt={clip.alt}
              className="relative aspect-[4/5] w-full rounded-sm bg-ink-900 shadow-lift sm:aspect-[3/4]"
            />

            {/* Overlapping editorial portrait — drifts against the clip behind
                it, which separates the two planes as the hero scrolls away. */}
            <Parallax
              speed={-26}
              className="absolute -bottom-10 -left-4 w-32 overflow-hidden rounded-sm border-4 border-canvas shadow-card sm:-left-10 sm:w-44"
            >
              <img
                src={portrait.src}
                alt={portrait.alt}
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
              />
            </Parallax>

            {/* Floating credential chip. */}
            <Parallax
              speed={18}
              className="absolute -right-2 bottom-16 flex items-center gap-2 rounded-full bg-canvas/95 px-4 py-2.5 shadow-card backdrop-blur sm:-right-8"
            >
              <Icon name="seal" size={16} className="text-sun-500" />
              <span className="text-[0.62rem] font-medium uppercase tracking-widest text-ink-900">
                Govt. Approved Valuer
              </span>
            </Parallax>
          </div>
        </Reveal>
      </div>

      {/* Scroll cue. */}
      <div className="shell relative hidden pb-10 lg:block">
        <a
          href="#collections"
          className="group inline-flex items-center gap-3 text-[0.65rem] uppercase tracking-mega text-ink-700/50 transition hover:text-ink-900"
        >
          <span className="relative grid h-9 w-9 place-items-center rounded-full border border-ink-900/15">
            <Icon name="arrowRight" size={15} className="rotate-90" />
            <span className="absolute inset-0 rounded-full border border-sun-400/60 animate-pulse-ring" />
          </span>
          Scroll to explore
        </a>
      </div>
    </section>
  );
}

export default Hero;
